import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DirTaskCategoryType, LeadType, ObjectType, TaskTypeDirType, UserType } from '@econtractor/types';
import { useAuth } from 'context/AuthContext';
import { isManagerUser } from 'utils/helpers/access/hasUserAccess';
import { useSettings } from 'context/SettingsContext';
import { useTask } from '../../../TaskContext';
import { pickUserRoleAccess } from '../../helpers/taskUsersHelpers';
import { toCategoryOptions, toStatusOptions, toTypeOptions } from '../helpers/toFilterOptions';
import type { TaskFilterProps } from './types';

const snapshotToUser = (user: UserType | null | undefined): UserType | null => {
    if (!user?.ID) return null;
    const access = pickUserRoleAccess(user.access);
    return {
        ID: Number(user.ID),
        userName: String(user.userName || ''),
        userEmail: String(user.userEmail || ''),
        userPicture: String(user.userPicture || ''),
        ...(user.teamName ? { teamName: user.teamName } : {}),
        ...(access ? { access } : {}),
    } as UserType;
};

const copyIds = (ids: number[] | null | undefined): number[] | null =>
    ids?.length ? [...ids] : null;

const useTaskFilter = ({
    excludeStatuses,
    showExecutorFilter = false,
    showControllerFilter = false,
    initialExecutorUser = null,
    initialControllerUser = null,
    initialLead = null,
    initialObject = null,
    onExecutorUserChange,
    onControllerUserChange,
    onLeadChange,
    onObjectChange,
}: TaskFilterProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { auth } = useAuth();
    const { localSettings } = useSettings();
    const {
        tasksStatus,
        setTasksStatus,
        tasksType,
        setTasksType,
        tasksCategory,
        setTasksCategory,
        tasksExecutor,
        setTasksExecutor,
        tasksController,
        setTasksController,
        filterLeadId,
        setFilterLeadId,
        filterObjectId,
        setFilterObjectId,
        onlyPersonal,
        setOnlyPersonal,
        onlyMine,
        setOnlyMine,
        canFilterRelated,
        setPage,
    } = useTask();
    const canPickRelated = canFilterRelated && isManagerUser(auth?.user);
    const canShowOnlyMine = showExecutorFilter || showControllerFilter;
    const myUserId = Number(auth?.user?.ID || 0);
    const isOtherUser = (user: UserType | null | undefined) =>
        Boolean(user?.ID && Number(user.ID) !== myUserId);
    const isOnlyMineLocked = (executor: UserType | null, controller: UserType | null) =>
        isOtherUser(executor) || isOtherUser(controller);
    const [show, setShow] = useState(false);
    const [executorUser, setExecutorUser] = useState<UserType | null>(() =>
        snapshotToUser(initialExecutorUser),
    );
    const [controllerUser, setControllerUser] = useState<UserType | null>(() =>
        snapshotToUser(initialControllerUser),
    );
    const [lead, setLead] = useState<LeadType | null>(() => (initialLead?.ID ? initialLead : null));
    const [object, setObject] = useState<ObjectType | null>(() =>
        initialObject?.ID ? initialObject : null,
    );
    const [draftTypes, setDraftTypes] = useState<number[] | null>(copyIds(tasksType));
    const [draftCategories, setDraftCategories] = useState<number[] | null>(copyIds(tasksCategory));
    const [draftStatuses, setDraftStatuses] = useState<number[] | null>(copyIds(tasksStatus));
    const [draftExecutorUser, setDraftExecutorUser] = useState<UserType | null>(() =>
        snapshotToUser(initialExecutorUser),
    );
    const [draftControllerUser, setDraftControllerUser] = useState<UserType | null>(() =>
        snapshotToUser(initialControllerUser),
    );
    const [draftLead, setDraftLead] = useState<LeadType | null>(() =>
        initialLead?.ID ? initialLead : null,
    );
    const [draftObject, setDraftObject] = useState<ObjectType | null>(() =>
        initialObject?.ID ? initialObject : null,
    );
    const [draftOnlyPersonal, setDraftOnlyPersonal] = useState(Boolean(onlyPersonal));
    const [draftOnlyMine, setDraftOnlyMine] = useState(() =>
        isOnlyMineLocked(
            snapshotToUser(initialExecutorUser),
            snapshotToUser(initialControllerUser),
        )
            ? false
            : onlyMine !== false,
    );
    const [draftExecutorPickerOpen, setDraftExecutorPickerOpen] = useState(() =>
        Boolean(snapshotToUser(initialExecutorUser)),
    );
    const [draftControllerPickerOpen, setDraftControllerPickerOpen] = useState(() =>
        Boolean(snapshotToUser(initialControllerUser)),
    );
    const [draftLeadPickerOpen, setDraftLeadPickerOpen] = useState(() => Boolean(initialLead?.ID));
    const [draftObjectPickerOpen, setDraftObjectPickerOpen] = useState(() =>
        Boolean(initialObject?.ID),
    );
    const excludeSet = useMemo(
        () => (excludeStatuses?.length ? new Set(excludeStatuses.map(Number)) : null),
        [excludeStatuses],
    );

    useEffect(() => {
        if (!tasksExecutor?.length && executorUser) {
            setExecutorUser(null);
            onExecutorUserChange?.(null);
        }
    }, [tasksExecutor, executorUser, onExecutorUserChange]);

    useEffect(() => {
        if (!tasksController?.length && controllerUser) {
            setControllerUser(null);
            onControllerUserChange?.(null);
        }
    }, [tasksController, controllerUser, onControllerUserChange]);

    useEffect(() => {
        if (!filterLeadId && lead) {
            setLead(null);
            onLeadChange?.(null);
        }
    }, [filterLeadId, lead, onLeadChange]);

    useEffect(() => {
        if (!filterObjectId && object) {
            setObject(null);
            onObjectChange?.(null);
        }
    }, [filterObjectId, object, onObjectChange]);

    const typeOptions = useMemo(
        () => toTypeOptions(localSettings?.DIRS?.TaskType as TaskTypeDirType[] | undefined, t),
        [localSettings?.DIRS?.TaskType, t],
    );

    const categoryOptions = useMemo(
        () => toCategoryOptions(localSettings?.DIRS?.TaskCategory as DirTaskCategoryType[] | undefined),
        [localSettings?.DIRS?.TaskCategory],
    );

    const statusOptions = useMemo(() => toStatusOptions(t, excludeSet), [t, excludeSet]);

    const selectedTypes = useMemo(
        () => typeOptions.filter((opt) => draftTypes?.includes(opt.value)),
        [typeOptions, draftTypes],
    );

    const selectedCategories = useMemo(
        () => categoryOptions.filter((opt) => draftCategories?.includes(opt.value)),
        [categoryOptions, draftCategories],
    );

    const selectedStatuses = useMemo(
        () => statusOptions.filter((opt) => draftStatuses?.includes(opt.value)),
        [statusOptions, draftStatuses],
    );

    const draftOnlyMineLocked = isOnlyMineLocked(draftExecutorUser, draftControllerUser);

    const hasActiveFilter = Boolean(
        tasksStatus?.length ||
            tasksType?.length ||
            tasksCategory?.length ||
            tasksExecutor?.length ||
            tasksController?.length ||
            filterLeadId ||
            filterObjectId ||
            onlyPersonal ||
            (canShowOnlyMine && onlyMine === false),
    );

    const handleHide = () => setShow(false);

    const handleOpen = () => {
        const nextExecutor = snapshotToUser(executorUser);
        const nextController = snapshotToUser(controllerUser);
        const nextLead = lead?.ID ? lead : null;
        const nextObject = object?.ID ? object : null;
        setDraftTypes(copyIds(tasksType));
        setDraftCategories(copyIds(tasksCategory));
        setDraftStatuses(copyIds(tasksStatus));
        setDraftExecutorUser(nextExecutor);
        setDraftControllerUser(nextController);
        setDraftLead(nextLead);
        setDraftObject(nextObject);
        setDraftOnlyPersonal(Boolean(onlyPersonal));
        const nextOnlyMineLocked = isOnlyMineLocked(nextExecutor, nextController);
        setDraftOnlyMine(nextOnlyMineLocked ? false : onlyMine !== false);
        setDraftExecutorPickerOpen(Boolean(nextExecutor));
        setDraftControllerPickerOpen(Boolean(nextController));
        setDraftLeadPickerOpen(Boolean(nextLead?.ID));
        setDraftObjectPickerOpen(Boolean(nextObject?.ID));
        setShow(true);
    };

    const handleChangeTypes = (ids: number[]) => {
        setDraftTypes(ids.length ? ids : null);
    };

    const handleChangeCategories = (ids: number[]) => {
        setDraftCategories(ids.length ? ids : null);
    };

    const handleChangeStatuses = (ids: number[]) => {
        setDraftStatuses(ids.length ? ids : null);
    };

    const handleChangeExecutor = (user: UserType | null) => {
        setDraftExecutorUser(user);
        if (!user) setDraftExecutorPickerOpen(false);
        if (isOnlyMineLocked(user, draftControllerUser)) setDraftOnlyMine(false);
    };

    const handleChangeController = (user: UserType | null) => {
        setDraftControllerUser(user);
        if (!user) setDraftControllerPickerOpen(false);
        if (isOnlyMineLocked(draftExecutorUser, user)) setDraftOnlyMine(false);
    };

    const handleChangeLead = (next: LeadType | null) => {
        setDraftLead(next);
        if (!next) setDraftLeadPickerOpen(false);
        if (next) setDraftOnlyPersonal(false);
    };

    const handleChangeObject = (next: ObjectType | null) => {
        setDraftObject(next);
        if (!next) setDraftObjectPickerOpen(false);
        if (next) setDraftOnlyPersonal(false);
    };

    const handleChangeOnlyPersonal = (checked: boolean) => {
        setDraftOnlyPersonal(checked);
        if (checked) {
            setDraftLead(null);
            setDraftObject(null);
            setDraftLeadPickerOpen(false);
            setDraftObjectPickerOpen(false);
        }
    };

    const handleApply = () => {
        setTasksType(copyIds(draftTypes));
        setTasksCategory(copyIds(draftCategories));
        setTasksStatus(copyIds(draftStatuses));

        if (showExecutorFilter) {
            setExecutorUser(draftExecutorUser);
            onExecutorUserChange?.(draftExecutorUser);
            setTasksExecutor(draftExecutorUser?.ID ? [Number(draftExecutorUser.ID)] : null);
        }

        if (showControllerFilter) {
            setControllerUser(draftControllerUser);
            onControllerUserChange?.(draftControllerUser);
            setTasksController(
                draftControllerUser?.ID ? [Number(draftControllerUser.ID)] : null,
            );
        }

        if (canShowOnlyMine) {
            const nextOnlyMineLocked = isOnlyMineLocked(
                showExecutorFilter ? draftExecutorUser : null,
                showControllerFilter ? draftControllerUser : null,
            );
            setOnlyMine(nextOnlyMineLocked ? false : Boolean(draftOnlyMine));
        }

        if (canFilterRelated) {
            const nextOnlyPersonal = draftOnlyPersonal;
            const nextLead = canPickRelated && !nextOnlyPersonal ? draftLead : null;
            const nextObject = canPickRelated && !nextOnlyPersonal ? draftObject : null;
            setLead(nextLead);
            onLeadChange?.(nextLead);
            setFilterLeadId(nextLead?.ID ? Number(nextLead.ID) : null);
            setObject(nextObject);
            onObjectChange?.(nextObject);
            setFilterObjectId(nextObject?.ID ? Number(nextObject.ID) : null);
            setOnlyPersonal(nextOnlyPersonal);
        }

        setPage(1);
        handleHide();
    };

    const handleReset = () => {
        setTasksType(null);
        setTasksCategory(null);
        setTasksStatus(null);
        setTasksExecutor(null);
        setTasksController(null);
        setFilterLeadId(null);
        setFilterObjectId(null);
        setOnlyPersonal(false);
        setOnlyMine(true);
        setExecutorUser(null);
        setControllerUser(null);
        setLead(null);
        setObject(null);
        onExecutorUserChange?.(null);
        onControllerUserChange?.(null);
        onLeadChange?.(null);
        onObjectChange?.(null);
        setPage(1);
        handleHide();
    };

    return {
        t,
        show,
        canFilterRelated,
        canPickRelated,
        canShowOnlyMine,
        typeOptions,
        categoryOptions,
        statusOptions,
        selectedTypes,
        selectedCategories,
        selectedStatuses,
        draftExecutorUser,
        draftControllerUser,
        draftLead,
        draftObject,
        draftOnlyPersonal,
        draftOnlyMine,
        draftOnlyMineLocked,
        draftExecutorPickerOpen,
        draftControllerPickerOpen,
        draftLeadPickerOpen,
        draftObjectPickerOpen,
        setDraftOnlyMine,
        setDraftExecutorPickerOpen,
        setDraftControllerPickerOpen,
        setDraftLeadPickerOpen,
        setDraftObjectPickerOpen,
        hasActiveFilter,
        handleHide,
        handleOpen,
        handleChangeTypes,
        handleChangeCategories,
        handleChangeStatuses,
        handleChangeExecutor,
        handleChangeController,
        handleChangeLead,
        handleChangeObject,
        handleChangeOnlyPersonal,
        handleApply,
        handleReset,
    };
};

export type TaskFilterModel = ReturnType<typeof useTaskFilter>;

export default useTaskFilter;
